import { z } from 'zod';
import { getCountryPhoneInfo, INDIA_PHONE_INFO } from '@/lib/constants';

// ====================
// Field-Level Validators
// ====================

// Phone Number Validation (10 digits, starts with 6-9) — used for domestic (non-NRI) leads
export const phoneValidator = z
  .string()
  .min(1, 'Mobile number is required')
  .regex(/^[6-9]\d{9}$/, 'Enter valid 10-digit mobile number starting with 6-9')
  .transform((val) => val.replace(/\s/g, '')); // Remove spaces

// Lenient digits-only validator — actual length/format is checked in
// superRefine below since it depends on whether the lead is NRI and, if so,
// which country (the form only ever sends raw digits, no country code).
const mobileDigitsValidator = z
  .string()
  .min(1, 'Mobile number is required')
  .regex(/^\d+$/, 'Mobile number must contain only digits');

const secondaryDigitsValidator = z
  .string()
  .regex(/^\d*$/, 'Mobile number must contain only digits')
  .optional()
  .or(z.literal(''));

/**
 * Shared NRI-aware phone length/format check, used by both the create and
 * edit lead schemas' superRefine.
 */
function validatePhoneFields(
  data: { mobile?: string; secondaryNumber?: string; isNRI?: boolean; nriCountry?: string },
  ctx: z.RefinementCtx
) {
  const isNRI = !!data.isNRI;
  const phoneInfo = isNRI ? getCountryPhoneInfo(data.nriCountry) : INDIA_PHONE_INFO;
  const lengthMessage =
    phoneInfo.minLength === phoneInfo.maxLength
      ? `Enter a valid ${phoneInfo.maxLength}-digit mobile number`
      : `Enter a valid mobile number (${phoneInfo.minLength}-${phoneInfo.maxLength} digits)`;

  if (data.mobile) {
    const valid = isNRI
      ? data.mobile.length >= phoneInfo.minLength && data.mobile.length <= phoneInfo.maxLength
      : /^[6-9]\d{9}$/.test(data.mobile);
    if (!valid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isNRI ? lengthMessage : 'Enter valid 10-digit mobile number starting with 6-9',
        path: ['mobile'],
      });
    }
  }

  if (data.secondaryNumber) {
    const valid = isNRI
      ? data.secondaryNumber.length >= phoneInfo.minLength && data.secondaryNumber.length <= phoneInfo.maxLength
      : /^[6-9]\d{9}$/.test(data.secondaryNumber);
    if (!valid) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: isNRI ? lengthMessage : 'Enter valid 10-digit mobile number',
        path: ['secondaryNumber'],
      });
    }
  }
}

// Email Validation (RFC compliant)
export const emailValidator = z
  .string()
  .email('Enter valid email address')
  .optional()
  .or(z.literal(''));

// PAN Card Validation (ABCDE1234F format)
export const panValidator = z
  .string()
  .regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Enter valid PAN (e.g., ABCDE1234F)')
  .toUpperCase()
  .optional()
  .or(z.literal(''));

// Pincode Validation (6 digits)
export const pincodeValidator = z
  .string()
  .regex(/^[1-9][0-9]{5}$/, 'Enter valid 6-digit pincode')
  .optional()
  .or(z.literal(''));

// CIBIL Score Validation (300-900)
export const cibilValidator = z
  .number()
  .min(300, 'CIBIL score must be at least 300')
  .max(900, 'CIBIL score cannot exceed 900')
  .optional()
  .or(z.literal(0))
  .or(z.nan());

// Loan Amount Validation (₹1L - ₹10Cr)
export const loanAmountValidator = z
  .number()
  .min(100000, 'Loan amount must be at least ₹1,00,000')
  .max(100000000, 'Loan amount cannot exceed ₹10,00,00,000')
  .optional()
  .or(z.literal(0))
  .or(z.nan());

// Loan Tenure Validation (1-30 years)
export const loanTenureValidator = z
  .number()
  .min(1, 'Tenure must be at least 1 year')
  .max(30, 'Tenure cannot exceed 30 years')
  .optional()
  .or(z.literal(0))
  .or(z.nan());

// Interest Rate Validation (0-30%)
export const interestRateValidator = z
  .number()
  .min(0, 'Interest rate cannot be negative')
  .max(30, 'Interest rate cannot exceed 30%')
  .optional()
  .or(z.literal(0))
  .or(z.nan());

// Percentage Validation (0-100%)
export const percentageValidator = z
  .number()
  .min(0, 'Percentage cannot be negative')
  .max(100, 'Percentage cannot exceed 100%')
  .optional()
  .or(z.literal(0))
  .or(z.nan());

// Date of Birth Validation (18+ years old)
export const dobValidator = z
  .date()
  .max(new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000), 'Customer must be at least 18 years old')
  .optional();

// Future Date Validation (for expected dates)
export const futureDateValidator = z
  .date()
  .min(new Date(), 'Date cannot be in the past')
  .optional();

// Non-empty String Validation
export const requiredStringValidator = z
  .string()
  .min(1, 'This field is required')
  .trim();

// Optional String Validation
export const optionalStringValidator = z
  .string()
  .trim()
  .optional()
  .or(z.literal(''));

// Years Validation (0-50 years)
export const yearsValidator = z
  .number()
  .min(0, 'Years cannot be negative')
  .max(50, 'Years cannot exceed 50')
  .optional()
  .or(z.literal(0))
  .or(z.nan());

// Lead Source Validation
export const leadSourceValidator = z
  .enum(['direct', 'referral', 'website', 'social_media', 'channel_partner', 'other'])
  .optional()
  .or(z.literal(''));

/**
 * Helper: Convert empty strings to undefined before coercing to number
 * This allows optional numeric fields to properly handle empty form inputs
 * Also converts NaN (from invalid input) to undefined to pass optional validation
 */
export const optionalNumberField = (validator: z.ZodSchema) =>
  z.preprocess(
    (val) => {
      // Convert empty strings to undefined
      if (val === '' || val === undefined) return undefined;
      // Try to coerce to number - if it's NaN, convert to undefined
      const num = Number(val);
      return isNaN(num) ? undefined : num;
    },
    z.number().pipe(validator).optional()
  );

// ====================
// Create Lead Schema
// ====================

export const createLeadSchema = z.object({
  // Required Fields
  firstName: requiredStringValidator,
  lastName: optionalStringValidator,
  mobile: mobileDigitsValidator,

  // Optional Customer Info
  secondaryNumber: secondaryDigitsValidator,
  email: emailValidator,
  dateOfBirth: z.coerce.date()
    .max(new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000), 'Customer must be at least 18 years old')
    .optional()
    .or(z.literal('')),
  panCard: panValidator,
  pincode: pincodeValidator,
  cibilScore: optionalNumberField(cibilValidator),

  // NRI / Residency
  isNRI: z.boolean().optional().default(false),
  nriCountry: optionalStringValidator,

  // Loan Details
  category: z.enum(['loan', 'insurance']).default('loan'),
  loanType: z.enum(['home_loan', 'lap', 'personal_loan', 'business_loan', 'car_loan'], {
    required_error: 'Loan type is required',
    invalid_type_error: 'Please select a valid loan type',
  }),
  loanAmount: z.coerce.number({
    required_error: 'Loan amount is required',
    invalid_type_error: 'Loan amount must be a number',
  }).min(100000, 'Loan amount must be at least ₹1,00,000').max(100000000, 'Loan amount cannot exceed ₹10,00,00,000'),
  loanTenure: z.coerce.number({
    required_error: 'Loan tenure is required',
    invalid_type_error: 'Loan tenure must be a number',
  }).min(1, 'Tenure must be at least 1 year').max(30, 'Tenure cannot exceed 30 years'),
  lender: optionalStringValidator,
  customerCity: requiredStringValidator.describe('Customer city is required'),
  
  // Employment
  employmentType: z.enum(['salaried', 'self_employed_itr', 'self_employed_pro']).optional(),
  monthlySalary: optionalNumberField(z.number()),
  
  // BT & Top-Up
  isBT: z.boolean().optional().default(false),
  isTopUp: z.boolean().optional().default(false),
  existingLender: optionalStringValidator,
  outstandingAmount: optionalNumberField(z.number()),
  currentInterestRate: optionalNumberField(interestRateValidator),
  remainingTenure: optionalNumberField(z.number()),
  topUpAmount: optionalNumberField(z.number()),
  
  // Relationships
  connectorId: optionalStringValidator,
  smContactId: optionalStringValidator,
  
  // Additional Employment/Business Info
  companyName: optionalStringValidator,
  yearsInCurrentJob: z.coerce.number().pipe(yearsValidator).optional(),
  leadSource: leadSourceValidator,
  
  // Assignment
  assignedTo: requiredStringValidator,
  sharedWith: z.array(z.string()).optional().default([]),
}).superRefine((data, ctx) => {
  validatePhoneFields(data, ctx);

  // Conditional validation: BT fields required when isBT is true
  if (data.isBT) {
    if (!data.existingLender) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Existing lender is required for Balance Transfer',
        path: ['existingLender'],
      });
    }
    if (!data.outstandingAmount || data.outstandingAmount <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Outstanding amount is required for Balance Transfer',
        path: ['outstandingAmount'],
      });
    }
  }
  
  // Conditional validation: Top-up amount required when isTopUp is true
  if (data.isTopUp) {
    if (!data.topUpAmount || data.topUpAmount <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Top-up amount is required',
        path: ['topUpAmount'],
      });
    }
  }
});

// ====================
// Edit Lead Schema
// ====================

export const editLeadSchema = z.object({
  // Customer Info
  firstName: requiredStringValidator,
  lastName: optionalStringValidator,
  mobile: mobileDigitsValidator,
  secondaryNumber: secondaryDigitsValidator,
  email: emailValidator,
  dateOfBirth: z.coerce.date()
    .max(new Date(Date.now() - 18 * 365 * 24 * 60 * 60 * 1000), 'Customer must be at least 18 years old')
    .optional()
    .or(z.literal('')),
  panCard: panValidator,
  pincode: pincodeValidator,
  cibilScore: optionalNumberField(cibilValidator),

  // NRI / Residency
  isNRI: z.boolean().optional(),
  nriCountry: optionalStringValidator,

  // Loan Details
  category: z.enum(['loan', 'insurance']).optional().or(z.literal('')),
  loanType: z.enum(['home_loan', 'lap', 'personal_loan', 'business_loan', 'car_loan']).optional().or(z.literal('')),
  loanAmount: optionalNumberField(loanAmountValidator),
  loanTenure: optionalNumberField(loanTenureValidator),
  lender: optionalStringValidator,
  customerCity: optionalStringValidator,
  
  // Employment
  employmentType: z.enum(['salaried', 'self_employed_itr', 'self_employed_pro']).optional().or(z.literal('')),
  monthlySalary: optionalNumberField(z.number()),
  
  // BT & Top-Up
  isBT: z.boolean().optional(),
  isTopUp: z.boolean().optional(),
  existingLender: optionalStringValidator,
  outstandingAmount: optionalNumberField(z.number()),
  currentInterestRate: optionalNumberField(interestRateValidator),
  remainingTenure: optionalNumberField(z.number()),
  topUpAmount: optionalNumberField(z.number()),
  
  // Financial Tracking
  sanctionedAmount: optionalNumberField(z.number()),
  disbursedAmount: optionalNumberField(z.number()),
  payoutPercentage: optionalNumberField(percentageValidator),
  vendorPayoutPercentage: optionalNumberField(percentageValidator),
  
  // Relationships
  connectorId: optionalStringValidator,
  smContactId: optionalStringValidator,
  
  // Additional Employment/Business Info
  companyName: optionalStringValidator,
  yearsInCurrentJob: optionalNumberField(yearsValidator),
  leadSource: leadSourceValidator,
  
  // Assignment
  assignedTo: requiredStringValidator,
  sharedWith: z.array(z.string()).optional(),
}).superRefine((data, ctx) => {
  validatePhoneFields(data, ctx);

  // Conditional validation: BT fields
  if (data.isBT) {
    if (!data.existingLender) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Existing lender is required for Balance Transfer',
        path: ['existingLender'],
      });
    }
    if (!data.outstandingAmount || data.outstandingAmount <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Outstanding amount is required for Balance Transfer',
        path: ['outstandingAmount'],
      });
    }
  }
  
  // Conditional validation: Top-up amount
  if (data.isTopUp) {
    if (!data.topUpAmount || data.topUpAmount <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Top-up amount is required',
        path: ['topUpAmount'],
      });
    }
  }
});

// ====================
// Disbursement Schema
// ====================

export const disbursementSchema = z.object({
  sanctionedAmount: z.coerce
    .number()
    .min(1, 'Sanctioned amount must be greater than 0'),
  disbursedAmount: z.coerce
    .number()
    .min(1, 'Disbursed amount must be greater than 0'),
  disbursementDate: z.coerce.date(),
  disbursementType: z.enum(['partial', 'final', 'full']),
  remarks: optionalStringValidator,
}).superRefine((data, ctx) => {
  // Validate disbursed amount doesn't exceed sanctioned
  if (data.disbursedAmount > data.sanctionedAmount) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Disbursed amount cannot exceed sanctioned amount',
      path: ['disbursedAmount'],
    });
  }
});

// ====================
// Helper Types
// ====================

export type CreateLeadFormData = z.infer<typeof createLeadSchema>;
export type EditLeadFormData = z.infer<typeof editLeadSchema>;
export type DisbursementFormData = z.infer<typeof disbursementSchema>;

// ====================
// Validation Helper Functions
// ====================

/**
 * Validate form data and return errors object
 */
export function validateFormData<T>(
  schema: z.ZodSchema<T>,
  data: unknown
): { success: true; data: T } | { success: false; errors: Record<string, string> } {
  const result = schema.safeParse(data);
  
  if (result.success) {
    return { success: true, data: result.data };
  }
  
  const errors: Record<string, string> = {};
  result.error.errors.forEach((err) => {
    const path = err.path.join('.');
    if (path) {
      errors[path] = err.message;
    }
  });
  
  return { success: false, errors };
}

/**
 * Get error message for a specific field
 */
export function getFieldError(errors: Record<string, string>, fieldName: string): string | undefined {
  return errors[fieldName];
}

/**
 * Check if field has error
 */
export function hasFieldError(errors: Record<string, string>, fieldName: string): boolean {
  return !!errors[fieldName];
}
